import { useMemo } from "react";
import { useCentralStore } from "@/dentlab/src/store/Central";
import {
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
} from "@mui/material";
import { TemplateItem } from "../../store/Templates/templates.store";
import { useCatalogsStore } from "../../store/Catalogs";
import { getTemplateItemDetails } from "../../store/Templates/template-item-details";
import { RoundToType } from "../../store/Jobs/utils";
import { ArticleTypeEnum } from "@/lib/supabase/supabaseEnums";
import { applyRounding, roundToCents } from "../../lib/utils/calculate";

interface JobItemsTemplatePreviewProps {
	templateItems: TemplateItem[];
	showTotals?: boolean;
	priceListId?: string | null;
	tpValue?: number;
	taxRate?: number;
	roundTo?: RoundToType;
}

export const JobItemsTemplatePreview: React.FC<
	JobItemsTemplatePreviewProps
> = ({
	templateItems,
	showTotals = false,
	priceListId,
	tpValue = 1,
	taxRate = 8.1,
	roundTo = RoundToType.TO_RAPPEN,
}) => {
	const { tariffsLookup } = useCatalogsStore((state) => ({
		tariffsLookup: state.tariffsLookup,
	}));
	const { articles } = useCentralStore((state) => ({
		articles: state.articles,
	}));

	const total = useMemo(() => {
		if (!showTotals || templateItems.length === 0) return null;

		let subTotal = 0;

		for (const item of templateItems) {
			if (item.tariffs_default_id || item.tariffs_custom_id) {
				const tariffId =
					item.tariffs_custom_id || item.tariffs_default_id;
				let tariff = null;

				for (const codeE in tariffsLookup) {
					if (tariffsLookup[codeE][tariffId || ""]) {
						tariff = tariffsLookup[codeE][tariffId || ""];
						break;
					}
				}

				if (tariff) {
					const priceItem = tariff.price_items?.find(
						(p) => p.price_list_id === priceListId
					);
					const price = priceItem?.price ?? 0;
					const tariffTpValue = tariff.own_tp ?? tpValue;
					subTotal += price * item.quantity * tariffTpValue;
				}
			} else if (item.article_id) {
				let article = null;

				for (const codeE in articles) {
					for (const id in articles[codeE]) {
						if (articles[codeE][id].id === item.article_id) {
							article = articles[codeE][id];
							break;
						}
					}
					if (article) break;
				}

				if (article && article.type !== ArticleTypeEnum.DISCOUNTS) {
					subTotal += article.price * item.quantity;
				}
			}
		}

		const roundedSubTotal = roundToCents(subTotal);
		const mwst = roundToCents(roundedSubTotal * (taxRate / 100));
		return applyRounding(roundedSubTotal + mwst, roundTo);
	}, [
		templateItems,
		tariffsLookup,
		articles,
		priceListId,
		tpValue,
		taxRate,
		roundTo,
		showTotals,
	]);

	if (templateItems.length === 0) {
		return (
			<div className="text-gray-500 text-sm p-4 text-center">
				Keine Positionen verfügbar
			</div>
		);
	}

	return (
		<div>
			<Table size="small" style={{ tableLayout: "fixed", width: "100%" }}>
				<TableHead>
					<TableRow>
						<TableCell
							style={{
								fontWeight: 600,
								padding: "8px",
								width: "80px",
							}}
						>
							Code
						</TableCell>
						<TableCell style={{ fontWeight: 600, padding: "8px" }}>
							Beschreibung
						</TableCell>
						<TableCell
							style={{
								fontWeight: 600,
								padding: "8px",
								width: "60px",
								textAlign: "right",
							}}
						>
							Menge
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{[...templateItems]
						.sort((a, b) => {
							const aDetails = getTemplateItemDetails(
								a,
								tariffsLookup,
								articles
							);
							const bDetails = getTemplateItemDetails(
								b,
								tariffsLookup,
								articles
							);
							return aDetails.code.localeCompare(bDetails.code);
						})
						.map((item) => {
							const { code, description } =
								getTemplateItemDetails(
									item,
									tariffsLookup,
									articles
								);

							return (
								<TableRow key={item.id} hover>
									<TableCell
										style={{
											padding: "8px",
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
										}}
										title={code}
									>
										{code}
									</TableCell>
									<TableCell
										style={{
											padding: "8px",
											whiteSpace: "nowrap",
											overflow: "hidden",
											textOverflow: "ellipsis",
										}}
										title={description}
									>
										{description}
									</TableCell>
									<TableCell
										style={{
											padding: "8px",
											textAlign: "right",
										}}
										title={item.quantity.toString()}
									>
										{item.quantity}
									</TableCell>
								</TableRow>
							);
						})}
				</TableBody>
			</Table>
			{showTotals && total !== null && (
				<div className="mt-3 flex justify-between items-center px-2 py-1 bg-gray-50 rounded text-sm font-medium">
					<span>Total (inkl. MwSt.)</span>
					<span>{total.toFixed(2)}</span>
				</div>
			)}
		</div>
	);
};
